"use client";

import { type InfoLineProps } from "#/components/InfoLine/InfoLine";
import { tooltips } from "#/config/tooltips";
import { type RFQResponseState } from "#/services/kyan/kyan.types";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useAccountState } from "#/store/wallet/account/accountState";
import { getImpactColor } from "#/utils/getImpactColor";

import { useRfqRiskData } from "../../../../../../hooks/useRfqRiskData";

export const usePortfolioImpactAndGreeksController = (
  response: RFQResponseState,
) => {
  const { market } = usePositionsDrawerMarket();
  const { portfolioState } = useAccountState(market.underlying);
  const { im, imr, mmr, delta, theta, gamma, vega, isLoading } = useRfqRiskData(
    {
      response,
      portfolioState,
      market,
    },
  );

  const marginData: InfoLineProps[] = [
    {
      data: {
        label: "Margin Usage (IMΔ)",
        amount: im - (portfolioState?.im ?? 0),
        unit: "USD",
      },
      tooltip: tooltips.marginUsage,
    },
    {
      data: {
        label: "Available Margin",
        amount: portfolioState.equity - im,
        unit: "USD",
      },
      tooltip: tooltips.availableMargin,
    },
    {
      data: {
        label: "IMr",
        change: {
          from: imr.from * 100,
          to: imr.to * 100,
          template: "0.00%",
        },
      },
      tooltip: (
        <>
          <strong>Initial Margin Ratio = Initial Margin / Equity.</strong>
          <br />A value of 100% means you have allocated all your Equity as
          margin, and have no available balance for increasing your position.
        </>
      ),
      leftLabelClassName: getImpactColor(imr.from ?? 0),
      rightLabelClassName: getImpactColor(imr.to),
    },
    {
      data: {
        label: "MMr",
        change: {
          from: (mmr?.from ?? 0) * 100,
          to: mmr.to * 100,
          template: "0.00%",
        },
      },
      tooltip: (
        <>
          <strong>
            Maintenance Margin Ratio = Maintenance Margin / Equity
          </strong>
          <br />A value of 100% means your account is under liquidation as there
          is not enough equity to maintain open positions.
        </>
      ),
      leftLabelClassName: getImpactColor(mmr?.from ?? 0),
      rightLabelClassName: getImpactColor(mmr.to),
    },
  ];

  const greeksData: InfoLineProps[] = [
    {
      data: {
        label: "Δ Delta",
        change: {
          ...delta,
          template: "0.0000",
        },
      },
      tooltip:
        "A measure of risk across your portfolio that changes dynamically with fluctuations in existing positions and the addition of new positions.",
    },
    {
      data: {
        label: "Γ Gamma",
        change: {
          ...gamma,
          template: "0.00000",
        },
      },
      tooltip: tooltips.gamma,
    },
    {
      data: {
        label: "V Vega",
        change: {
          ...vega,
          template: "0.00",
        },
      },
      tooltip: tooltips.vega,
    },
    {
      data: {
        label: "Θ Theta",
        change: {
          ...theta,
          template: "0.00",
        },
      },
      tooltip: tooltips.theta,
    },
  ];

  return {
    marginData,
    greeksData,
    isLoading,
  };
};
